export enum PaperSize {
  Letter = 'letter',
  A4 = 'a4',
  Legal = 'legal'
}

export enum PaperOrientation {
  Portrait = 'portrait',
  Landscape = 'landscape'
}

/**
 * How a reader wants the document laid out on paper. These never reach the report service — the
 * document that comes back is the same one whatever paper it is destined for.
 */
export type PageSetup = {
  paper: PaperSize
  orientation: PaperOrientation
  onePagePerField: boolean
}

export const defaultPageSetup: PageSetup = {
  paper: PaperSize.Letter,
  orientation: PaperOrientation.Portrait,
  onePagePerField: false
}

/**
 * `name` is what `@page` understands; the measurements are for the sheet on screen. Paper geometry has
 * one source of truth so the preview and the printout cannot disagree about what a page holds.
 */
const paperGeometry: Record<PaperSize, { name: string; shortEdge: string; longEdge: string }> = {
  [PaperSize.Letter]: { name: 'letter', shortEdge: '8.5in', longEdge: '11in' },
  [PaperSize.A4]: { name: 'A4', shortEdge: '210mm', longEdge: '297mm' },
  [PaperSize.Legal]: { name: 'legal', shortEdge: '8.5in', longEdge: '14in' }
}

export const sheetWidth = ({ paper, orientation }: PageSetup): string =>
  orientation === PaperOrientation.Landscape
    ? paperGeometry[paper].longEdge
    : paperGeometry[paper].shortEdge

/**
 * The paper the printout asks for. Only `size` is set here; the margin stays in the stylesheet, where
 * the rest of the document's geometry lives.
 */
export const pageSizeRule = ({ paper, orientation }: PageSetup): string =>
  `@page report { size: ${paperGeometry[paper].name} ${orientation}; }`
